import { createClientExecutionUid } from './clientExecutionUid';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';
import type { ExecutionRelation } from './executionAuthorityResolver';

export type LegacyAllocationDraft = {
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
};

export type CargoAllocationSummary = {
  cargoId: number;
  total: number;
  distributed: number;
  remaining: number;
};

export type MultiEdgeValidationIssue = {
  code:
    | 'MISSING_LOADING_STOP'
    | 'MISSING_UNLOADING_STOP'
    | 'INVALID_QTY'
    | 'ZERO_ALLOCATION'
    | 'QTY_EXCEEDS_CAP'
    | 'QTY_EXCEEDS_AVAILABLE';
  message: string;
  cargoId?: number;
  edgeUid?: string;
};

export function makeAllocationEdgeUid(): string {
  return createClientExecutionUid();
}

export function createAllocationEdge(params: {
  cargoId: number;
  fromStopUid: string;
  toStopUid: string;
  quantity: number;
  edgeUid?: string;
}): AllocationEdgeDraft {
  return {
    edgeUid: params.edgeUid ?? makeAllocationEdgeUid(),
    cargoId: Number(params.cargoId),
    fromStopUid: String(params.fromStopUid),
    toStopUid: String(params.toStopUid),
    quantity: Math.max(0, Number(params.quantity) || 0)
  };
}

/** Legacy single-edge maps → edge list (one edge per cargo with qty > 0). */
export function legacyDraftsToAllocationEdges(legacy: LegacyAllocationDraft): AllocationEdgeDraft[] {
  const edges: AllocationEdgeDraft[] = [];
  const seenCargo = new Set<number>();

  for (const cargoId of legacy.selectedIds) {
    const cid = Number(cargoId);
    if (!Number.isFinite(cid) || seenCargo.has(cid)) continue;
    seenCargo.add(cid);

    const qty = Number(legacy.quantityDraft[cid] ?? 0);
    if (qty <= 0) continue;

    const fromStopUid = legacy.fromStopDraft[cid];
    const toStopUid = legacy.toStopDraft[cid];
    if (!fromStopUid || !toStopUid) continue;

    edges.push(
      createAllocationEdge({
        cargoId: cid,
        fromStopUid,
        toStopUid,
        quantity: qty
      })
    );
  }

  return edges;
}

/** Collapse edges to legacy maps (last edge wins per cargo — for bridge only). */
export function allocationEdgesToLegacyDrafts(
  edges: AllocationEdgeDraft[],
  selectedIds?: number[]
): LegacyAllocationDraft {
  const fromStopDraft: FromStopDraftByUid = {};
  const toStopDraft: ToStopDraftByUid = {};
  const quantityDraft: QuantityDraftByCargo = {};
  const cargoSet = new Set<number>();

  for (const edge of edges) {
    if (edge.quantity <= 0) continue;
    const cid = Number(edge.cargoId);
    cargoSet.add(cid);
    quantityDraft[cid] = (quantityDraft[cid] ?? 0) + edge.quantity;
    fromStopDraft[cid] = edge.fromStopUid;
    toStopDraft[cid] = edge.toStopUid;
  }

  const mergedSelected =
    selectedIds != null
      ? Array.from(
          new Set(
            selectedIds.map(Number).filter(Number.isFinite).concat(Array.from(cargoSet))
          )
        )
      : Array.from(cargoSet);

  return {
    selectedIds: mergedSelected,
    fromStopDraft,
    toStopDraft,
    quantityDraft
  };
}

export function ensureAllocationEdgesFromLegacy(
  edges: AllocationEdgeDraft[],
  legacy: LegacyAllocationDraft
): AllocationEdgeDraft[] {
  if (edges.length > 0) return edges;
  return legacyDraftsToAllocationEdges(legacy);
}

export function selectedCargoIdsFromEdges(edges: AllocationEdgeDraft[]): number[] {
  const ids = new Set<number>();
  for (const e of edges) {
    if (e.quantity > 0) ids.add(Number(e.cargoId));
  }
  return Array.from(ids);
}

export function sumQuantityByCargo(edges: AllocationEdgeDraft[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const e of edges) {
    if (e.quantity <= 0) continue;
    const cid = Number(e.cargoId);
    map.set(cid, (map.get(cid) ?? 0) + e.quantity);
  }
  return map;
}

export function computeCargoAllocationSummary(params: {
  cargoId: number;
  edges: AllocationEdgeDraft[];
  totalQuantity: number;
}): CargoAllocationSummary {
  const cid = Number(params.cargoId);
  const total = Math.max(0, Number(params.totalQuantity) || 0);
  const distributed = params.edges
    .filter((e) => Number(e.cargoId) === cid && e.quantity > 0)
    .reduce((sum, e) => sum + e.quantity, 0);
  return {
    cargoId: cid,
    total,
    distributed,
    remaining: Math.max(0, total - distributed)
  };
}

export function edgesForCargoAtLoading(params: {
  edges: AllocationEdgeDraft[];
  cargoId: number;
  loadingStopUid: string;
}): AllocationEdgeDraft[] {
  const cid = Number(params.cargoId);
  return params.edges.filter(
    (e) => Number(e.cargoId) === cid && e.fromStopUid === params.loadingStopUid
  );
}

export function activeEdgesAtLoading(
  edges: AllocationEdgeDraft[],
  loadingStopUid: string
): AllocationEdgeDraft[] {
  return edges.filter((e) => e.fromStopUid === loadingStopUid && e.quantity > 0);
}

export function determineExecutionRelationsFromEdges(
  edges: AllocationEdgeDraft[],
  positionByStopUid: Map<string, number>
): ExecutionRelation[] {
  const relations: ExecutionRelation[] = [];

  for (const edge of edges) {
    const qty = Number(edge.quantity ?? 0);
    if (qty <= 0) continue;

    const fromStopUid = edge.fromStopUid;
    const toStopUid = edge.toStopUid;
    if (!fromStopUid || !toStopUid) continue;

    const fromPosition = positionByStopUid.get(fromStopUid);
    const toPosition = positionByStopUid.get(toStopUid);
    if (fromPosition === undefined || toPosition === undefined) continue;

    relations.push({
      cargoId: Number(edge.cargoId),
      fromStopUid,
      toStopUid,
      quantity: qty,
      fromPosition,
      toPosition
    });
  }

  return relations;
}

export function mapAllocationEdgesToStopIds(params: {
  edges: AllocationEdgeDraft[];
  positionByStopUid: Map<string, number>;
  savedStops: Array<{
    id: number;
    position: number;
    clientExecutionUid?: string;
    stopUid?: string;
  }>;
}): Array<{ cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }> {
  const idByPosition = new Map(params.savedStops.map((s) => [s.position, s.id]));
  const idByUid = new Map<string, number>();
  for (const stop of params.savedStops) {
    const uid = String(stop.clientExecutionUid ?? stop.stopUid ?? '').trim();
    if (uid) idByUid.set(uid, stop.id);
  }

  const resolveStopId = (uid: string | undefined, position: number | undefined): number | undefined => {
    if (uid) {
      const byUid = idByUid.get(uid);
      if (byUid != null) return byUid;
    }
    if (position !== undefined) {
      const byPos = idByPosition.get(position);
      if (byPos != null) return byPos;
    }
    return undefined;
  };

  const rows: Array<{ cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }> = [];

  for (const edge of params.edges) {
    const quantity = Number(edge.quantity ?? 0);
    if (quantity <= 0) continue;

    const fromPos = params.positionByStopUid.get(edge.fromStopUid);
    const toPos = params.positionByStopUid.get(edge.toStopUid);

    rows.push({
      cargoId: Number(edge.cargoId),
      quantity,
      fromStopId: resolveStopId(edge.fromStopUid, fromPos),
      toStopId: resolveStopId(edge.toStopUid, toPos)
    });
  }

  return rows;
}

export type ApiAllocationRow = {
  cargoId?: number;
  cargo?: { id?: number };
  allocatedQuantity?: number;
  fromStopId?: number;
  toStopId?: number;
};

export function hydrateAllocationEdgesFromApi(params: {
  apiAllocations: ApiAllocationRow[];
  stopIdToUid: Map<number, string>;
  defaultFromStopUid?: string;
  defaultToStopUid?: string;
}): AllocationEdgeDraft[] {
  const edges: AllocationEdgeDraft[] = [];

  for (const row of params.apiAllocations) {
    const cargoId = Number(row.cargoId ?? row.cargo?.id);
    if (!Number.isFinite(cargoId)) continue;

    const qty = Number(row.allocatedQuantity ?? 0);
    if (qty <= 0) continue;

    const fromUid =
      row.fromStopId != null
        ? params.stopIdToUid.get(Number(row.fromStopId))
        : undefined;
    const toUid =
      row.toStopId != null ? params.stopIdToUid.get(Number(row.toStopId)) : undefined;

    edges.push(
      createAllocationEdge({
        cargoId,
        fromStopUid: fromUid ?? params.defaultFromStopUid ?? '',
        toStopUid: toUid ?? params.defaultToStopUid ?? '',
        quantity: qty
      })
    );
  }

  return edges.filter((e) => e.fromStopUid && e.toStopUid);
}

export function validateMultiEdgeAllocations(params: {
  edges: AllocationEdgeDraft[];
  cargoCapById: Record<number, number | undefined>;
  cargoAvailableById?: Record<number, number | undefined>;
}): MultiEdgeValidationIssue[] {
  const issues: MultiEdgeValidationIssue[] = [];

  for (const edge of params.edges) {
    const qty = Number(edge.quantity ?? 0);
    const cid = Number(edge.cargoId);

    if (qty < 0) {
      issues.push({
        code: 'INVALID_QTY',
        cargoId: cid,
        edgeUid: edge.edgeUid,
        message: `Груз #${cid}: количество не может быть отрицательным`
      });
      continue;
    }

    if (qty === 0) continue;

    if (!edge.fromStopUid?.trim()) {
      issues.push({
        code: 'MISSING_LOADING_STOP',
        cargoId: cid,
        edgeUid: edge.edgeUid,
        message: `Груз #${cid}: не указана точка погрузки`
      });
    }
    if (!edge.toStopUid?.trim()) {
      issues.push({
        code: 'MISSING_UNLOADING_STOP',
        cargoId: cid,
        edgeUid: edge.edgeUid,
        message: `Груз #${cid}: не указана точка выгрузки`
      });
    }
  }

  const sumByCargo = sumQuantityByCargo(params.edges);

  for (const [cargoId, distributed] of Array.from(sumByCargo.entries())) {
    const cap = params.cargoCapById[cargoId];
    if (cap != null && distributed > cap) {
      issues.push({
        code: 'QTY_EXCEEDS_CAP',
        cargoId,
        message: `Груз #${cargoId}: распределено ${distributed} мест, всего ${cap}`
      });
    }

    const available = params.cargoAvailableById?.[cargoId];
    if (available != null && distributed > available) {
      issues.push({
        code: 'QTY_EXCEEDS_AVAILABLE',
        cargoId,
        message: `Груз #${cargoId}: распределено ${distributed}, доступно ${available}`
      });
    }
  }

  const hasPositive = params.edges.some((e) => e.quantity > 0);
  if (!hasPositive && params.edges.length > 0) {
    issues.push({
      code: 'ZERO_ALLOCATION',
      message: 'Укажите количество больше 0 хотя бы для одной строки распределения'
    });
  }

  return issues;
}

export function formatMultiEdgeValidationMessage(issues: MultiEdgeValidationIssue[]): string | null {
  if (issues.length === 0) return null;
  return issues[0].message;
}

export function upsertEdgeInList(
  edges: AllocationEdgeDraft[],
  updated: AllocationEdgeDraft
): AllocationEdgeDraft[] {
  const idx = edges.findIndex((e) => e.edgeUid === updated.edgeUid);
  if (idx < 0) return [...edges, updated];
  const next = [...edges];
  next[idx] = updated;
  return next;
}

export function removeEdgeFromList(
  edges: AllocationEdgeDraft[],
  edgeUid: string
): AllocationEdgeDraft[] {
  return edges.filter((e) => e.edgeUid !== edgeUid);
}

export function addSplitEdgeForCargo(params: {
  edges: AllocationEdgeDraft[];
  cargoId: number;
  fromStopUid: string;
  defaultToStopUid: string;
  totalQuantity: number;
}): AllocationEdgeDraft[] {
  const summary = computeCargoAllocationSummary({
    cargoId: params.cargoId,
    edges: params.edges,
    totalQuantity: params.totalQuantity
  });
  const splitQty = summary.remaining > 0 ? summary.remaining : 0;
  if (splitQty <= 0) return params.edges;

  return [
    ...params.edges,
    createAllocationEdge({
      cargoId: params.cargoId,
      fromStopUid: params.fromStopUid,
      toStopUid: params.defaultToStopUid,
      quantity: splitQty
    })
  ];
}

export function setCargoEdgesAtLoading(params: {
  edges: AllocationEdgeDraft[];
  cargoId: number;
  loadingStopUid: string;
  newEdgesAtLoading: AllocationEdgeDraft[];
}): AllocationEdgeDraft[] {
  const cid = Number(params.cargoId);
  const others = params.edges.filter(
    (e) => !(Number(e.cargoId) === cid && e.fromStopUid === params.loadingStopUid)
  );
  return [...others, ...params.newEdgesAtLoading];
}
